import React from 'react';
import { motion } from 'framer-motion';
import { Link2Icon } from 'lucide-react';
import { useLaunch } from '../contexts/LaunchContext';
import { StoreBadges } from './StoreBadges';
export function HeroSection() {
  const {
    isPublicLaunch
  } = useLaunch();
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth'
  });
  return <section className="relative pt-36 pb-16 sm:pt-36 sm:pb-20 lg:pt-44 lg:pb-28 overflow-hidden" data-id="element-124" data-mp="2wjp9tfzgmtzg">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-125" data-mp="30lkdus8rzlgx">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-kitty-yellow-100/40 blur-3xl" data-id="element-126" data-mp="2mufi418j3dbv" />
        <div className="absolute top-[40%] -left-[10%] w-[50%] h-[50%] rounded-full bg-blue-50/40 blur-3xl" data-id="element-127" data-mp="2rrywr2951s4c" />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" data-id="element-128" data-mp="3fh05yrpsp7ao">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center" data-id="element-129" data-mp="3k9jlvvbygein">
          {/* Left Column */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-xl" data-id="element-130" data-mp="2fgjz2wfxvko4">
            <motion.p initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.1,
            duration: 0.4
          }} className="inline-flex items-center gap-2 bg-kitty-yellow-100 text-kitty-yellow-800 rounded-full px-4 py-1.5 text-sm font-semibold mb-6" data-id="element-131" data-mp="2p6danxg2zzsd">
              <span className="w-2 h-2 rounded-full bg-kitty-yellow-600" data-id="element-132" data-mp="2u6iu4qc76fzd" />
              {isPublicLaunch ? 'Available on iOS and Android' : 'UK early access — limited spots'}
            </motion.p>

            {/* Headline */}
            <h1 className="text-[2rem] sm:text-[2.5rem] lg:text-5xl xl:text-6xl font-extrabold text-kitty-dark leading-[1.1] mb-6 lg:mb-6 tracking-tight" data-id="element-133" data-mp="1dr7srm7dpuo6">
              Group money made simple
            </h1>

            {/* Subheading */}
            <p className="text-lg lg:text-xl font-medium text-gray-700 mb-5 leading-relaxed max-w-md" data-id="element-134" data-mp="38vo02qs7zlbl">
              Create shared kitties, track expenses, settle up together.
            </p>

            <div className="max-w-md mb-7 rounded-2xl border border-kitty-yellow-200 bg-white px-4 py-3.5 shadow-sm" data-id="element-135" data-mp="3itlopfdzrk4q">
              <div className="flex items-start gap-3" data-id="element-136" data-mp="3rs708ql1pyw0">
                <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-kitty-yellow text-kitty-dark" data-id="element-137" data-mp="3rrnvkbkep6zx">
                  <Link2Icon className="h-4 w-4" aria-hidden="true" data-id="element-138" data-mp="5cmb6qiyvz8c" />
                </div>
                <p className="text-sm sm:text-base font-bold leading-snug text-kitty-dark" data-id="element-139" data-mp="5eysr2ktc7kv">
                  Friends can take part straight from their browser. Open the
                  invite and join in seconds.
                </p>
              </div>
            </div>

            {/* Supporting copy */}
            <p className="text-base text-gray-500 mb-8 lg:mb-10 leading-relaxed max-w-md" data-id="element-140" data-mp="23ueprhc9k0sd">
              Start a kitty for a trip, night out, house share or event. Add
              what you spend, choose who paid and who shares it, and KittyPot
              keeps track of who owes what.
            </p>

            {/* CTA */}
            {isPublicLaunch ? <div className="mb-6" data-id="element-141" data-mp="1pwhpa9uova8f">
                <StoreBadges tone="light" data-id="element-142" data-mp="2mkwaozquts36" />
              </div> : <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-143" data-mp="2mmqolu3pkjxr">
                <button onClick={() => scrollTo('get-started')} className="w-full sm:w-auto bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-[16px] lg:py-4 min-h-[56px] rounded-full font-bold transition-colors duration-150 active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-dark" data-id="element-144" data-mp="2ijudeoghzq3u">
                  Join the waitlist
                </button>
                <button onClick={() => scrollTo('how-it-works')} className="w-full sm:w-auto bg-white hover:bg-gray-50 text-kitty-dark px-8 py-[16px] lg:py-4 min-h-[56px] rounded-full font-bold transition-colors duration-150 active:scale-[0.98] border border-gray-200 shadow-sm flex items-center justify-center gap-2 text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-145" data-mp="2l4s4ganxipsq">
                  See how it works
                </button>
              </div>}

            <p className="mb-5 max-w-md text-xs text-gray-500" data-id="element-146" data-mp="2v1n10o0n6g4d">
              {isPublicLaunch ? <>
                  Using KittyPot is subject to our{' '}
                  <a href="/terms" className="font-semibold text-kitty-dark underline underline-offset-2 transition-colors hover:text-kitty-yellow-700" data-id="element-147" data-mp="3npsld9nsa33o">
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a href="/privacy-policy" className="font-semibold text-kitty-dark underline underline-offset-2 transition-colors hover:text-kitty-yellow-700" data-id="element-148" data-mp="1obfxdi0c2xu">
                    Privacy Policy
                  </a>
                  .
                </> : <>
                  We use your details to contact you about the waitlist. See our{' '}
                  <a href="/privacy-policy" className="font-semibold text-kitty-dark underline underline-offset-2 transition-colors hover:text-kitty-yellow-700" data-id="element-149" data-mp="3evwo6678dq6m">
                    Privacy Policy
                  </a>
                  .
                </>}
            </p>
          </motion.div>

          {/* Right Column: App Mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          rotate: 2
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          type: 'spring'
        }} className="relative mx-auto w-full max-w-[240px] sm:max-w-[260px] lg:max-w-[320px] mt-8 lg:mt-0" data-id="element-150" data-mp="134j2o2ys3ga2">
            <div className="overflow-hidden rounded-[2.25rem] border-[5px] border-kitty-dark bg-kitty-dark shadow-2xl" data-id="element-151" data-mp="1p5hv54cptsp8">
              <img src="https://cdn.magicpatterns.com/uploads/oThRvtimoMpejsSpVpekoB/WhatsApp_Image_2026-08-26_at_19.59.45.jpg" alt="The KittyPot app showing the Ski Trip 2027 kitty: total spent, the number of people sharing it, and controls to add an expense or a receipt" className="block h-auto w-full" data-id="element-152" data-mp="ri9fhpzebdv5" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}